<template>
    <v-card class="panelBlock" v-if="isVisibleEventsPanel">
        <v-flex tag="h2" data-qe-id="upcoming_events">{{title}}</v-flex>
        <v-flex class="upcoming-events">
            <status-overlay v-if="isVisibleEventsPanel" :action="loadingAction"></status-overlay>
            <v-flex>
                <v-layout
                    row
                    wrap
                    class="time_block"
                    v-for="(event, idx) in upcomingEventsForDisplay"
                    :key="idx">
                    <v-flex xs3 class="date">
                        <v-flex tag="span">
                            <span>{{getDayName(event.startDateTime)}}</span>
                            <span v-if="eventEndDay(event)">{{' - ' + eventEndDay(event)}}</span>
                        </v-flex>
                        <v-flex tag="strong">{{getDayOfMonth(event.startDateTime)}}</v-flex>
                        <v-flex tag="small">{{getMonthYearTimeForEvents(event.startDateTime, event.endDateTime)}}</v-flex>
                    </v-flex>
                    <v-flex xs9>
                        <v-layout row wrap>
                            <v-flex xs9 class="desc">
                                <a @click="goToEvent(event.eventUrl)"
                                :data-qe-id="`upcoming-event_item_${idx}_event-name_link`"
                                class="no-decoration">
                                    {{event.eventName}}
                                </a>
                            </v-flex>
                            <v-flex xs3 text-xs-center>
                                <v-avatar size="45"><img :src="sizeFormattedImageUrl(event.fallbackEventImageURL, 100, 100)" alt="" /></v-avatar>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap class="member__details">
                            <v-flex xs5 text-truncate v-if="event.registeredUsers && event.registeredUsers.length">
                                <v-avatar size="25">
                                  <img :src="sizeFormattedImageUrl(event.registeredUsers[0].userProfileImageUrl, 50, 50)"
                                       :onerror="imageErrorFallback(sizeFormattedImageUrl(event.registeredUsers[0].fallbackUserProfileImageUrl, 50, 50))">
                                </v-avatar>
                                <v-flex tag="span">{{event.registeredUsers[0].preferredName}}</v-flex>
                            </v-flex>
                            <v-flex xs5 text-truncate v-if="event.registeredUsers && event.registeredUsers.length > 1">
                                <v-avatar size="25">
                                  <img :src="sizeFormattedImageUrl(event.registeredUsers[1].userProfileImageUrl, 50, 50)"
                                       :onerror="imageErrorFallback(sizeFormattedImageUrl(event.registeredUsers[1].fallbackUserProfileImageUrl, 50, 50))">
                                </v-avatar>
                                <v-flex tag="span">{{event.registeredUsers[1].preferredName}}</v-flex>
                            </v-flex>
                            <v-flex xs2 text-truncate>
                                <v-btn flat icon v-if="event.registrationCount-2 > 0">+{{event.registrationCount-2}}</v-btn>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap class="action-btns">
                            <v-flex v-if="event.isRegistered">
                                <v-btn small class="active" :data-qe-id="`upcoming-event_item_${idx}_registered_btn`">
                                    <i class="fas fa-check-circle"></i>
                                    {{upcomingEventsConstant.REGISTERED}}
                                </v-btn>
                            </v-flex>
                            <v-flex v-else>
                                <a small class="no-decoration action-link" :data-qe-id="`upcoming-event_item_${idx}_register-event_link`" @click="goToEvent(event.registerUrl)">
                                    {{upcomingEventsConstant.REGISTER}}
                                </a>
                            </v-flex>
                            <v-flex small v-if="event.isRegistered">
                                <a class="no-decoration action-link" @click="goToEvent(event.eventUrl)" :data-qe-id="`upcoming-event_item_${idx}_edit-registration_link`">
                                    {{upcomingEventsConstant.EDIT_REGISTERED}}
                                </a>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                    <v-layout
                        v-if="isCommunityAdmin && isChapterPage && currentCommunityView === userRoles.ADMIN"
                        row
                        wrap
                        class="status">
                        <v-flex md6><span :class="[`status__${event.eventStatus}`]">{{event.eventStatus}}</span></v-flex>
                        <v-flex md6 text-xs-right>
                            <v-btn class="primary_btn small ma-0" :data-qe-id="`upcoming-event_item_${idx}_manage-event_btn`" @click="openLink(`${appCommonConstants.MANAGE_EVENT_GOTO_LINK}${event.id}`)">
                                {{appCommonConstants.MANAGE_EVENT}}
                            </v-btn>
                        </v-flex>
                    </v-layout>
                </v-layout>
            </v-flex>
            <v-layout row wrap class="status-more upcoming-events__create-new-event">
                <v-flex text-xs-right>
                    <v-btn class="primary_btn small transparent" @click="seeMore()"
                    data-qe-id="upcoming-event_see-more_btn">
                        {{appCommonConstants.SEE_MORE}}
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-flex>
    </v-card>
</template>

<script>
    import { mapActions, mapGetters, mapState } from 'vuex';
    import {
      upcomingEventsConstant,
      createFilterForUpComingEvents,
      createFilterForRegisteredEvents,
      createCommunityFilter,
    } from '@/components/ChapterPage/communityCommon.constant';
    import { storeAction } from '@/store/storeActions.constant';
    import { storeGetters } from '@/store/storeGetters.constant';
    import { getDayName, getDayOfMonth, getMonthYearTime, getMonthYearTimeForEvents } from "@/common/utils/dateTime";
    import { appCommonConstants, userRoles, routerNames } from '@/components/appCommon.constants';
    import StatusOverlay from '@/components/StatusOverlay/StatusOverlay';
    import { imageErrorFallback } from "@/common/utils/utils";
    import { eventDirectoryPageConstant } from "../../EventDirectoryPage/eventDirectoryPage.constants";
    import { gaDataLayerEvent } from '@/plugins/analytics';
    import { eventTileConstants, getAttributionTracking } from "@/components/YpoSearch/ypoSearch.constants";
    import { gtmEventName } from "@/plugins/gtmAnalytics";
    import { ypoEventConstant } from "@/components/YpoEvents/ypoEvents.constant";
    import { myToolsConstants } from '../../Tools/mytools.constant';
    import { sizeFormattedImageUrl } from "@/common/utils/cloudinaryUtils";

    export default {
        name: "UpcomingEvent",
        components: {
            StatusOverlay,
        },
        props: {
            isConsiderTheseEventGadget: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                dialogState: {
                    isVisible: false,
                },
                routerNames,
                userRoles,
                isSaved: true,
                isVisibleEventsPanel: true,
                getDayName,
                getDayOfMonth,
                getMonthYearTime,
                getMonthYearTimeForEvents,
                appCommonConstants,
                upcomingEventsConstant,
                displayCount: appCommonConstants.EVENT_COUNT_MIN,
                canSeeMore: false,
                defaultFilterKey: eventDirectoryPageConstant.constant.myChapterDefaultFilter.parentKey,
                sizeFormattedImageUrl,
            };
        },
        created() {
            if (this.isConsiderTheseEventGadget) {
                this.getUnRegisteredEvents();
            } else {
                this.getUpcomingEventList();
            }
        },
        watch: {
            upcomingEventList(val) {
                if (!this.isConsiderTheseEventGadget) {
                    if (val) {
                        this.isVisibleEventsPanel = val.numberOfEvents > 0;
                        this.canSeeMore = val.numberOfEvents > 3;
                    } else {
                        this.isVisibleEventsPanel = false;
                    }
                }
            },
            unRegisteredEvents(val) {
                if (this.isConsiderTheseEventGadget) {
                    if (val) {
                        this.isVisibleEventsPanel = val.length > 0;
                        this.canSeeMore = val.length > 3;
                    } else {
                        this.isVisibleEventsPanel = false;
                    }
                }
            },
        },
        methods: {
            ...mapActions({
                getUpcomingEventList: `chapter/${storeAction.getUpcomingEventList}`,
                getUnRegisteredEvents: `chapter/${storeAction.getUnRegisteredEvents}`,
                saveUpcomingEvent: `chapter/${storeAction.saveUpcomingEvent}`,
                rejectUpcomingEvent: `chapter/${storeAction.rejectUpcomingEvent}`,
            }),
            imageErrorFallback,
            eventEndDay(event) {
                if (event && event.endDateTime && event.startDateTime) {
                    if (getDayOfMonth(event.endDateTime) - getDayOfMonth(event.startDateTime) > 0 && getDayName(event.endDateTime) !== getDayName(event.startDateTime)) {
                        return getDayName(event.endDateTime);
                    }
                }
                return null;
            },
            openLink(link) {
                window.open(link, '_blank');
                return false;
            },
          goToEvent(link) {
            const baseAttributionTrackingId = this.$route && this.$route.name === routerNames.HOME
                ? eventTileConstants.ATTRIBUTION_TRACKING.homeEventWidgetId
                : eventTileConstants.ATTRIBUTION_TRACKING.communityEventWidgetId;
            const url = getAttributionTracking(link, baseAttributionTrackingId);
            gaDataLayerEvent(gtmEventName.C_Consider_Events, link);
            window.open(url, this.isMobileAppBrowser ? '_self' : '_blank');
          },
            seeMore() {
                if (this.isConsiderTheseEventGadget) {
                    this.$router.push({
                      path: `/${routerNames.YPO_EVENTS}`,
                      query: {
                        eventFilters: JSON.stringify({...ypoEventConstant.defaultEventFilters}),
                        eventSearchText: '',
                      },
                    });
                } else {
                    const filters = [];
                    const communityData = this.communityInfo;
                    if (this.$route.name === this.routerNames.HOME) {
                      this.defaultFilterKey = upcomingEventsConstant.Sponsors;
                        const myEventsFilter = {
                            "name": upcomingEventsConstant.EVENT_NAME,
                            "searchFilterKey": upcomingEventsConstant.SEARCH_FILTER_KEY,
                            "searchFilterValue": this.currentUserId,
                            "searchRibbonGroupName": upcomingEventsConstant.SEARCH_RIBBON_GROUP_NAME,
                        };
                        filters.push(createFilterForRegisteredEvents(myEventsFilter));
                    } else if (this.isAllNetworks) {
                        this.usersCommunitiesList.filter(x => x.communityType === this.routerNames.NETWORK)
                            .forEach((community) => {
                                if (community.searchRibbonGroupName !== "") {
                                    filters.push(createFilterForUpComingEvents(community, true));
                                }
                            });
                    } else if (communityData) {
                      this.defaultFilterKey = (communityData.communityType.toLowerCase() === 'chapter'  || communityData.communityType.toLowerCase() === 'network' )
                      && communityData.isMember
                          ? `my${communityData.communityType}s` : eventDirectoryPageConstant.constant.myChapterDefaultFilter.parentKey;
                      filters.push(createCommunityFilter(communityData));
                    }

                    const eventFilters = {
                        [this.defaultFilterKey]: filters,
                    };

                    this.$router.push({
                        path: `/${routerNames.YPO_EVENTS}`,
                        query: {
                            eventFilters: JSON.stringify(eventFilters),
                            eventSearchText: '',
                        },
                    });
                }
            },
        },
        computed: {
            ...mapState('actionState', ['actions']),
            ...mapGetters({
                upcomingEventList: `chapter/${storeGetters.upcomingEventList}`,
                salesforceURL: `${storeGetters.salesforceURL}`,
                isCommunityAdmin: `chapter/${storeGetters.getIsCommunityAdmin}`,
                currentCommunityView: `${storeGetters.currentCommunityView}`,
                usersCommunitiesList: `chapter/${storeGetters.usersCommunitiesList}`,
                communityInfo: `chapter/${storeGetters.communityInfo}`,
                currentUserId: `memberProfile/${storeGetters.currentUserId}`,
                unRegisteredEvents: `chapter/${storeGetters.unRegisteredEvents}`,
                isMobileAppBrowser: `${[storeGetters.isMobileAppBrowser]}`,
            }),
            isChapterPage() {
                return this.$route.name === this.routerNames.CHAPTER;
            },
            isAllNetworks() {
                return this.usersCommunitiesList && this.usersCommunitiesList.some(o => o.communityId === this.$route.params.communityId && o.name.toLowerCase() === myToolsConstants.YOUR_NETWORKS_CONSOLIDATED_LOWER_CASE);
            },
            upcomingEventsForDisplay() {
                let events = [];
                if (this.isConsiderTheseEventGadget && this.unRegisteredEvents) {
                    events = this.unRegisteredEvents;
                    events = events.slice(0, this.displayCount);
                } else if (!this.isConsiderTheseEventGadget && this.upcomingEventList) {
                    events = this.upcomingEventList;
                    events = events.slice(0, this.displayCount);
                }
                return events;
            },
            numberOfSelectedEvents() {
                return this.upcomingEventsForDisplay.length;
            },
            eventsCount() {
                let result = true;
                if (this.isConsiderTheseEventGadget && this.unRegisteredEvents &&
                    this.numberOfSelectedEvents > this.appCommonConstants.EVENT_COUNT_MIN) {
                        result = false;
                } else if (!this.isConsiderTheseEventGadget && this.upcomingEventList && this.numberOfSelectedEvents &&
                    this.numberOfSelectedEvents > this.appCommonConstants.EVENT_COUNT_MIN) {
                    result = false;
                }
                return result;
            },
            loadingAction() {
                return this.actions[storeAction.getUpcomingEventList] || this.actions[storeAction.getUnRegisteredEvents];
            },
            title() {
                if (this.isConsiderTheseEventGadget) {
                    return this.upcomingEventsConstant.CONSIDER_THESE_EVENTS;
                }
                return this.upcomingEventsConstant[`${this.$route.name.toUpperCase()}_TITLE`];
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import "../../../common/styles/_colors.scss";

    .upcoming-events {
        min-height: 80px;
        position: relative;

        &__create-new-event {
            margin: 5px 0;
        }

        &__dropdown {
            padding: 10px;
        }

        .status-more {
            padding: 0 0 0 11px;
            align-items: center;
        }
    }

    .time_block {
        margin: 20px 10px 10px 10px;
        padding: 10px 5px;
        border-radius: 4px;
        box-shadow: 0 1px 3px 0 rgba($black, 0.18);
        border: 1px solid rgba($ypo-gray, 0.4);

        .date {
            color: $ypo-dark-background-gray;
            line-height: 12px;
            padding-left: 5px;

            span {
                font-size: 11px;
                display: block;
                font-weight: 600;
                line-height: normal;
            }

            strong {
                color: $ypo_blue;
                font-family: "Noto Sans";
                font-size: 28px;
                display: block;
                line-height: normal;
                padding: 4px 0;
                font-weight: 400;
            }

            small {
                color: $ypo-dark-background-gray;
                font-size: 10px;
                font-weight: bold;
            }
        }

        .desc {
            color: $ypo_blue;
            font-family: "Noto Sans";
            font-size: 12px;
            font-weight: bold;
            letter-spacing: -0.15px;
            line-height: 17px;
            margin-bottom: 30px;
        }

        .v-avatar {
            border: 1px solid $ypo-medium-gray;
        }

        .member__details {
            span {
                color: $ypo-dark-background-gray;
                font-size: 12px;
                letter-spacing: -0.15px;
                line-height: 15px;
                padding-left: 5px;
            }

            .v-btn {
                width: auto;
                height: auto;
                color: $ypo-dark-background-gray;
                font-size: 12px;
                letter-spacing: 0.4px;
                line-height: 16px;
                text-align: right;

                &:hover {
                    &:before {
                        background-color: transparent;
                    }

                    &:active {
                        background-color: transparent;
                    }
                }

                &:focus {
                    background-color: transparent;
                }
            }
        }

        .action-btns {
            border-top: 1px solid $ypo-medium-gray;
            margin-top: 10px;
            padding-top: 5px;
            text-align: left;
            line-height: 30px;

            .v-btn {
                padding: 0;
                text-transform: capitalize;
                min-width: auto;
                background: transparent !important;
                box-shadow: none !important;
                height: auto;
                color: $ypo-dark-background-gray;
                font-size: 12px;
                margin: 0;

                &.active {
                    color: $ypo-green;

                    i {
                        color: $ypo-green;
                        margin-top: 0;
                        font-size: 14px;
                        margin-right: 3px;
                    }
                }

                &:hover {
                    &:before {
                        background-color: transparent;
                    }

                    &:active {
                        background-color: transparent;
                    }
                }

                &:focus {
                    background-color: transparent;
                }
            }

            .action-link {
                color: $ypo-dark-background-gray;
                font-size: 12px;
                line-height: 22px;
            }

            i {
                font-size: 20px;
                color: $ypo-cta;
                margin-top: 5px;
                cursor: pointer;
            }
        }

        .status {
            padding: 10px 5px 0;
            align-items: center;

            &__Active {
                color: $ypo-green;
            }

            &__Pending {
                color: $black;
            }

            &__Completed {
                color: $ypo-gray;
            }
        }
    }

    .rec_events {
        .v-card {
            margin: 10px;
            border: 1px solid rgba($ypo-gray, 0.2);
            border-radius: 4px;
            background-color: rgba($ypo-gray, 0.1);
            box-shadow: none;
            min-height: 230px;
            font-size: 12px;

            span {
                color: $ypo-blue;
            }

            .headline {
                color: $white;
                position: relative;
                font-size: 16px !important;
                left: 10px;
                top: 5px;
            }
        }

        .image-overlay {
            &:before {
                background: linear-gradient(0deg, rgba($black, 0) 0%, $black 100%);
                content: "";
                width: 100%;
                height: 100%;
                left: 0;
                top: 0;
                position: absolute;
            }
        }

        .fill-height {
            padding: 0;

            .v-icon {
                font-size: 14px;
                color: $white;
                position: absolute;
                right: 5px;
                top: 5px;
                cursor: pointer;
                opacity: 0.3;
            }
        }

        .v-card__title {
            min-height: 100px;
            align-items: flex-start;
        }
    }
</style>
